import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import './AuditLogs.css';
import { apiService } from '../../../services/api';
import Pagination from '../shared/Pagination';
import Timestamp from '../shared/Timestamp';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const pageSize = 10;

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'Authentication', label: 'Authentication' },
    { id: 'Data', label: 'Data' },
    { id: 'Query', label: 'Queries' },
    { id: 'Automation', label: 'Automations' },
    { id: 'System', label: 'System Events' },
  ];

  const loadAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        page_size: pageSize,
      };

      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      const response = await apiService.getAuditLogs(params);

      const items = response.items || [];
      const total = response.total || 0;
      const calculatedTotalPages = response.total_pages || Math.ceil(total / pageSize) || 1;

      setLogs(items);
      setTotalPages(calculatedTotalPages);
      setTotalItems(total);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, currentPage, pageSize]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter]);

  useEffect(() => {
    if (currentPage !== 1) return;

    const checkForNewLogs = async () => {
      try {
        const params = { page: 1, page_size: pageSize };
        if (categoryFilter !== 'all') {
          params.category = categoryFilter;
        }
        const response = await apiService.getAuditLogs(params);
        const newTotal = response.total || 0;
        const newItems = response.items || [];

        if (newTotal !== totalItems || (newItems[0]?.id !== logs[0]?.id)) {
          setLogs(newItems);
          setTotalItems(newTotal);
          setTotalPages(response.total_pages || Math.ceil(newTotal / pageSize) || 1);
        }
      } catch (err) {
        console.debug('Background poll failed:', err);
      }
    };

    const interval = setInterval(checkForNewLogs, 10000);
    return () => clearInterval(interval);
  }, [currentPage, categoryFilter, pageSize, totalItems, logs]);

  const normalizeStatus = (status) => {
    if (!status) return '-';
    const first = status.split('\n')[0].trim().toLowerCase();
    if (first === 'success' || first === 'succeeded' || first === 'complete' || first === 'completed') {
      return 'Succeeded';
    }
    if (first === 'failed' || first === 'fail' || first === 'error') {
      return 'Failed';
    }
    return status.split('\n')[0];
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    const normalized = normalizeStatus(status).toLowerCase();
    if (normalized === 'succeeded') return 'audit-logs-status-success';
    if (normalized === 'failed') return 'audit-logs-status-failed';
    return '';
  };

  // Audit category -> Figma activity-type chip variant (PRD-1361).
  const getCategoryVariant = (category) => {
    if (!category) return 'standard';
    switch (category.toLowerCase()) {
      case 'authentication': return 'authentication';
      case 'data': return 'data';
      case 'automation': return 'automations';
      case 'query': return 'queries';
      case 'system': return 'system';
      default: return 'standard';
    }
  };

  /**
   * Build the full details text for a log entry.
   * Combines the explicit `details` field with any extra lines from a multiline `status`
   * (backwards-compat for older logs that stored details inside the status field).
   */
  const getDetailsText = (log) => {
    const parts = [];
    if (log.details) parts.push(log.details);

    // Legacy: older logs stored extra info as additional lines in status
    if (log.status && log.status.includes('\n')) {
      const extraLines = log.status.split('\n').slice(1).filter(l => l.trim());
      if (extraLines.length > 0) {
        parts.push(...extraLines);
      }
    }
    return parts;
  };

  const hasExpandableDetails = (log) => {
    const parts = getDetailsText(log);
    return parts.length > 1 || (parts.length === 1 && parts[0].includes('\n'));
  };

  const getDetailsSummary = (log) => {
    const parts = getDetailsText(log);
    if (parts.length === 0) return '-';
    // Show first line as the summary
    return parts[0].split('\n')[0];
  };

  const getExpandedLines = (log) => {
    const parts = getDetailsText(log);
    if (parts.length <= 1 && !(parts[0] || '').includes('\n')) return [];
    // Flatten all parts into lines, skip the first line (already shown as summary)
    const allLines = parts.flatMap(p => String(p).split('\n'));
    return allLines.slice(1).filter(l => l.trim());
  };

  const toggleRow = (logId) => {
    setExpandedRows((prev) => ({ ...prev, [logId]: !prev[logId] }));
  };

  return (
    <div className="audit-logs-page">
      <div className="audit-logs-filters">
        <div className="audit-logs-filter-group">
          <select
            className="resui-select resui-select--compact audit-logs-filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div className="audit-logs-results-count">
          {totalItems} {totalItems === 1 ? 'log' : 'logs'} found
        </div>
      </div>

      {loading ? (
        <div className="audit-logs-loading">Loading audit logs...</div>
      ) : error ? (
        <div className="audit-logs-error">{error}</div>
      ) : logs.length === 0 ? (
        <div className="audit-logs-empty">No audit logs found</div>
      ) : (
        <div className="audit-logs-table-container">
          <table className="audit-logs-table">
            <thead>
              <tr>
                <th>TIME</th>
                <th>ACTOR</th>
                <th>CATEGORY</th>
                <th>ACTION</th>
                <th>STATUS</th>
                <th>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const expandable = hasExpandableDetails(log);
                const isExpanded = expandedRows[log.id];
                const expandedLines = expandable ? getExpandedLines(log) : [];
                return (
                  <tr key={log.id} className={isExpanded ? 'audit-logs-row-expanded' : ''}>
                    <td className="audit-logs-time"><Timestamp value={log.time} form="static" /></td>
                    <td className="audit-logs-actor">{log.actor || 'System'}</td>
                    <td>
                      <Chip variant={getCategoryVariant(log.category)} className="audit-logs-category">
                        {log.category || '-'}
                      </Chip>
                    </td>
                    <td className="audit-logs-action">{log.action || '-'}</td>
                    <td className="audit-logs-status-cell">
                      <span className={`audit-logs-status-label ${getStatusClass(log.status)}`}>
                        {normalizeStatus(log.status)}
                      </span>
                    </td>
                    <td className="audit-logs-target">
                      {expandable && expandedLines.length > 0 ? (
                        <div>
                          <div className="audit-logs-details-header" onClick={() => toggleRow(log.id)}>
                            <span>{getDetailsSummary(log)}</span>
                            <Icon
                              name={isExpanded ? 'expand_less' : 'expand_more'}
                              size={20}
                              className={`audit-logs-expand-icon ${isExpanded ? 'expanded' : ''}`}
                            />
                          </div>
                          {isExpanded && (
                            <div className="audit-logs-expanded-content">
                              {expandedLines.map((line, idx) => (
                                <div key={idx} className="audit-logs-expanded-line">{line}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span>{getDetailsSummary(log)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}
    </div>
  );
};

export default AuditLogs;
